import { useEffect, useState, useMemo } from "react";
import DeckGL, { MapController } from "deck.gl";
import { MapView, WebMercatorViewport } from "@deck.gl/core";

import { renderAllLayers } from "./render_layers";

const App = () => {
  // view_state
  const [lat_init, lon_init] = [35.68143607425954, 136.10797821712563];
  const zoom_init = 4.5;
  const viewstate_init = {
    width: window.innerWidth,
    height: window.innerHeight - 40,
    longitude: lon_init,
    latitude: lat_init,
    zoom: zoom_init,
    minZoom: 3,
    maxZoom: 17,
  };
  const [viewState, setViewState] = useState(viewstate_init);
  const views = useMemo(() => new MapView({ repeat: true }), []);
  const onViewStateChange = (v) => {
    setViewState(v.viewState);
  };

  // lineData
  const [lineData, setLineData] = useState(null);
  useEffect(() => {
    // const url = "https://7yo7bckemvgq7fegwdzl6mkveu0qzkoy.lambda-url.ap-northeast-1.on.aws/line";
    const url = "/data/line.json";

    fetch(url)
      .then(res => {
        if (!res.ok) {
          return null;
        } else {
          return res.json();
        }
      })
      .then(json => {
        setLineData(json);
      })
      .catch(() => {
        setLineData(null);
      });
  }, []);

  // lineCdとlineIndexのマッピングデータ
  const lineCdLineIndexData = useMemo(() => {
    if (!lineData) return null;

    return Object.fromEntries(
      lineData.map(({line_cd}, index) => [line_cd, index])
    );
  }, [lineData]);

  // LineIndex
  const [lineIndex, setLineIndex] = useState(0);

  // Lineプルダウンでの選択が変更された時のlineIndexとclickedStationの変更処理
  const onChangeLineSelect = ({target}) => {
    const v = target.value;

    setLineIndex(Number(v));
    setClickedStation(null);
  };

  // LineCD
  const lineCd = useMemo(() => {
    if (!lineData) return null;

    return lineData[lineIndex].line_cd;
  }, [lineData, lineIndex]);

  // LineStationRouteData
  const [lineStationRouteData, setLineStationRouteData] = useState(null);
  useEffect(() => {
    if (!lineCd) return;

    // const url = `https://7yo7bckemvgq7fegwdzl6mkveu0qzkoy.lambda-url.ap-northeast-1.on.aws/line_station_route?line_cd=${lineCd}`;
    const url = `/data/line_station_route/${lineCd}.json`;

    fetch(url)
      .then(res => {
        if (!res.ok) {
          return null;
        } else {
          return res.json();
        }
      })
      .then(json => {
        setLineStationRouteData(json);
      })
      .catch(() => {
        setLineStationRouteData(null);
      });
  }, [lineCd]);

  // クリックした駅
  const [clickedStation, setClickedStation] = useState(null);

  // 地図のクリックコールバック
  const clickMapHandler = ({ object, layer }) => {
    if (!object || !layer) return;

    if (layer.id === "station-layer") {
      setClickedStation(object);
    }
  };

  // クリックした駅の乗り換え可能路線
  const [transferableLineData, setTransferableLineData] = useState(null);
  useEffect(() => {
    if (!clickedStation) return;

    // const url = `https://7yo7bckemvgq7fegwdzl6mkveu0qzkoy.lambda-url.ap-northeast-1.on.aws/station_g_line_path?station_g_cd=${clickedStation.station_g_cd}`;
    const url = `/data/station_g_line_path/${clickedStation.station_g_cd}.json`;

    fetch(url)
      .then(res => {
        if (!res.ok) {
          return null;
        } else {
          return res.json();
        }
      })
      .then(json => {
        setTransferableLineData(json);
      })
      .catch(() => {
        setTransferableLineData(null);
      });

  }, [clickedStation]);

  // 乗り換え可能路線ダイアログの閉じるボタンのonClick
  const onClickClickedStationClose = () => {
    setClickedStation(null);
  };

  // 乗り換え可能路線のクリックコールバック
  const onClickTransferableLine = (line_cd) => {
    const line_index = lineCdLineIndexData[line_cd];
    setLineIndex(line_index);
    setFitZoneLine(true);
  };

  // bboxにfitするviewport
  const bboxFitViewPort = (bbox) => {
    const [minLon, minLat, maxLon, maxLat] = bbox;

    // viewState から bbox に収まるようにフィットさせる
    const viewport = new WebMercatorViewport({
      width: viewState.width,
      height: viewState.height,
      longitude: viewState.longitude,
      latitude: viewState.latitude,
      zoom: viewState.zoom,
    });
    const viewportFitBounds = viewport.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      {
        padding: 40,
        maxZoom: 15,
      }
    );

    return viewportFitBounds;
  };

  // lineStationRouteDataが変わったら、地図レイヤーを更新する
  const layers = useMemo(() => {
    if (!lineStationRouteData) {
      return [];
    }

    // Stationデータ
    const stationData = lineStationRouteData.station;

    // Routeデータ
    const routeData = lineStationRouteData.route;

    // lineにfitするzoomを求める
    const {zoom} = bboxFitViewPort(lineStationRouteData.bbox);

    return renderAllLayers({ stationData: stationData, routeData: routeData, clickedStation: clickedStation, lineCd: lineCd, zoom: zoom });
  }, [lineStationRouteData, clickedStation]);

  // fitZoneLineするかのフラグ
  const [fitZoneLine, setFitZoneLine] = useState(false);

  // lineStationRouteDataが変わったら、fitBoundする
  useEffect(() => {
    if (!lineStationRouteData && !fitZoneLine) return;

    const transitionDuration = 800;

    // lineにfitするviewportを求める
    const viewportFitBounds = bboxFitViewPort(lineStationRouteData.bbox);

    // viewStateの更新
    setViewState(v => {
      return {
        ...v,
        longitude: viewportFitBounds.longitude,
        latitude: viewportFitBounds.latitude,
        zoom: viewportFitBounds.zoom,
        transitionDuration: transitionDuration,
      };
    });

    // fitZoneLineの更新
    setFitZoneLine(false);

  }, [lineStationRouteData, fitZoneLine]);

  // カーソル
  const [cursor, setCursor] = useState("grab");

  // hoverカーソル
  const hoverMapHandler = ({ object, layer }) => {
    if (layer && layer.id === "station-layer") {
      setCursor("pointer");
    } else {
      setCursor("grab");
    }
  };

  // ツールチップ
  const getTooltip = ({object, layer}) => {
    if (!object) return null;
    if (!layer || layer.id !== "station-layer") return null;

    return {
      text: object.station_name
    };
  };

  return (
    <div className="App">
      <div className="map-container">
        <DeckGL
          layers={layers}
          controller={{ type: MapController, dragRotate: false }}
          initialViewState={viewstate_init}
          viewState={viewState}
          views={views}
          onViewStateChange={onViewStateChange}
          getCursor={() => cursor}
          onHover={hoverMapHandler}
          onClick={clickMapHandler}
          getTooltip={getTooltip}
        />
      </div>

      {
        lineData &&
          <div className="line-panel">
            Railroad Line:
            <select
              id="line-select"
              value={lineIndex ?? ""}
              onChange={onChangeLineSelect}
            >
              {
                lineData.map(({line_cd, line_name}, index) => <option key={line_cd} value={index}>{line_name}</option>)
              }
            </select>
          </div>
      }

      {
        clickedStation && (
          <div className="station-panel">
            <div>
              <h3 className="station-name">{clickedStation.station_name}</h3>
              <button className="station-close" onClick={onClickClickedStationClose}>×</button>
              <div>
                {
                  transferableLineData && (
                    <ul className="transfer-list">
                      {
                        transferableLineData.map(({line_cd, line_name}) =>
                          <li
                            key={line_cd}
                            value={line_name}
                            onClick={() => onClickTransferableLine(line_cd)}
                          >
                            {line_name}
                          </li>
                        )
                      }
                    </ul>
                  )
                }
              </div>
            </div>
          </div>
        )
      }

      <div className="map-attribution">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          ©︎ OpenStreetMap contributors
        </a>
      </div>

    </div>
  );
};

export default App;
